/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * InviteReviewPage -- R13 web Round 2.
 *
 * Web parity for the mobile Smart Invite review screen. Reuses the
 * documented contract in docs/connect/MOBILE_API.md:
 *   POST   /connect/contacts/:id/pitch/generate
 *   GET    /connect/pitches/:id
 *   PATCH  /connect/pitches/:id
 *   POST   /connect/pitches/:id/regenerate (?refresh_items=true optional)
 *   POST   /connect/pitches/:id/preview-link
 *   POST   /connect/pitches/:id/send   (channel email|whatsapp|both)
 *
 * Access is already gated by ConnectLayout (features.connect) and by
 * `requireConnectAccess` server-side. We do no client-side owner
 * check -- the server owns authorisation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import connectApi from '../../services/connectApi';

const SKD_CYAN_DARK = '#0e7490';

// Platform roadmap engine target completion is 12-20 s p95. Give up
// gracefully after 2 minutes -- the sweeper on the server will flip
// stuck pending rows to 'failed' within another 5 min window.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

const SECTION_KEYS = [
  { k: 'roadmap',   label: 'Roadmap' },
  { k: 'insights',  label: 'Insights' },
  { k: 'research',  label: 'Research' },
  { k: 'services',  label: 'Services' },
  { k: 'ai',        label: 'AI solutions' },
  { k: 'grow',      label: 'Grow' },
  { k: 'resources', label: 'Resources' },
  { k: 'cta',       label: 'Call-to-action' }
];

const INTENTS = [
  { v: '',                           label: 'Not set' },
  { v: 'upskill_team',               label: 'Upskill team' },
  { v: 'ai_adoption',                label: 'AI adoption' },
  { v: 'digital_transformation',     label: 'Digital transformation' },
  { v: 'business_growth',            label: 'Business growth' },
  { v: 'pilot_project',              label: 'Pilot project' },
  { v: 'faculty_upskilling',         label: 'Faculty upskilling (university / institute)' },
  { v: 'industry_ready_curriculum',  label: 'Industry-ready curriculum' },
  { v: 'hiring_placement',           label: 'Hiring / placement' },
  { v: 'partnership',                label: 'Partnership' }
];

export default function InviteReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contact, setContact] = useState(null);
  const [pitch, setPitch]     = useState(null);
  const [draft, setDraft]     = useState(null);
  const [focus, setFocus]     = useState(null);
  const [bootErr, setBootErr] = useState(null);

  // ── Polling bookkeeping ──
  // start-time ref MUST NOT reset on each poll; it is set once when
  // we first observe 'pending' and compared against Date.now() on
  // every tick.
  const pollStartRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollingRef   = useRef(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // ── Save / regenerate / preview / send transient state ──
  const [saveBusy, setSaveBusy]        = useState(false);
  const [saveErr, setSaveErr]          = useState(null);
  const [saveFieldErr, setSaveFieldErr] = useState(null); // { field, error }
  const [saveNote, setSaveNote]        = useState(null);

  const [focusBusy, setFocusBusy] = useState(false);
  const [focusErr, setFocusErr]   = useState(null);
  const [focusNote, setFocusNote] = useState(null);

  const [regenBusy, setRegenBusy] = useState(false);
  const [regenErr, setRegenErr]   = useState(null);

  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr]   = useState(null);

  // Send flow: channel + optional allow_despite_intro re-prompt.
  const [sendOpen, setSendOpen]       = useState(false);
  const [sendChannel, setSendChannel] = useState('email');
  const [sendBusy, setSendBusy]       = useState(false);
  const [sendErr, setSendErr]         = useState(null);
  const [sendPrompt, setSendPrompt]   = useState(null); // { kind, message, retry }
  const [sendResult, setSendResult]   = useState(null);

  // ── Boot: load contact, resolve draft pitch ──
  const bootstrap = useCallback(async () => {
    setBootErr(null); setPitch(null); setDraft(null); setContact(null); setFocus(null);
    try {
      const c = await connectApi.getContact(id);
      setContact(c);
      setFocus({
        industry:    c.industry    || '',
        intent:      c.intent      || '',
        challenge:   c.challenge   || '',
        refine_note: c.refine_note || '',
        // R15 Phase 1 (2026-10-09): the API accepts `next_step` +
        // `due_date` as aliases on INPUT; the server stores them in
        // the pre-existing `next_action_note` + `follow_up_date`
        // columns (migration 034). READS come back on those column
        // names. The catalog intro email renders them as
        // {{next_steps}} + {{due_date}} when
        // CONNECT_EMAIL_CATALOG_ENABLED=true.
        next_step: c.next_action_note || '',
        due_date:  c.follow_up_date ? String(c.follow_up_date).slice(0, 10) : ''
      });

      // Mobile contract: latest_pitch?.status === 'draft' means the
      // existing draft is editable. Anything else -> mint a new one
      // (the API is idempotent while a draft exists and creates a
      // fresh draft after send/expire).
      const latest = c.latest_pitch || null;
      let pitchId = null;
      if (latest && latest.status === 'draft' && latest.id) {
        pitchId = latest.id;
      } else {
        const minted = await connectApi.generatePitch(id);
        pitchId = minted && (minted.id || minted._id);
      }
      if (!pitchId) throw new Error('Could not prepare an invite draft.');
      const row = await connectApi.getPitch(pitchId);
      _applyPitch(row);
    } catch (e) {
      setBootErr(_friendlyErr(e, 'Could not load this invite.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // ── Apply a pitch row to local state (also resets the editable
  //    draft to match server after regenerate/save). ──
  const _applyPitch = useCallback((row) => {
    setPitch(row);
    setDraft({
      subject:        row.subject || '',
      personal_lines: row.personal_lines || '',
      sections_included: _normaliseSections(row.sections_included),
      offering_ids:   new Set((row.offering_ids || []).map(String)),
      insight_ids:    new Set((row.insight_ids  || []).map(String))
    });
    setSaveErr(null); setSaveFieldErr(null); setSaveNote(null);
    if (row.roadmap_status === 'pending') {
      if (!pollStartRef.current) pollStartRef.current = Date.now();
      setPollTimedOut(false);
      _startPolling(row.id);
    } else {
      _stopPolling();
      pollStartRef.current = null;
      setPollTimedOut(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Polling. Uses setTimeout so the next tick is scheduled AFTER
  //    the previous fetch resolves (no overlapping requests). Pauses
  //    on tab-hidden; resumes on visible. ──
  const _stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
  }, []);

  const _tick = useCallback(async (pitchId) => {
    if (!pollingRef.current) return;
    if (typeof document !== 'undefined' && document.hidden) {
      // Pause; the visibilitychange handler will resume us.
      return;
    }
    const start = pollStartRef.current || Date.now();
    if (Date.now() - start > POLL_TIMEOUT_MS) {
      _stopPolling();
      setPollTimedOut(true);
      return;
    }
    try {
      const row = await connectApi.getPitch(pitchId);
      // Guard against stale ticks after unmount / nav.
      if (!pollingRef.current) return;
      _applyPitch(row);
      // Keep ticking only while still pending.
      if (row.roadmap_status === 'pending' && pollingRef.current) {
        pollTimerRef.current = setTimeout(() => _tick(pitchId), POLL_INTERVAL_MS);
      } else {
        _stopPolling();
      }
    } catch (e) {
      // Transient error -- keep trying until the 2-minute window ends.
      if (pollingRef.current) {
        pollTimerRef.current = setTimeout(() => _tick(pitchId), POLL_INTERVAL_MS);
      }
    }
  }, [_applyPitch, _stopPolling]);

  const _startPolling = useCallback((pitchId) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    pollTimerRef.current = setTimeout(() => _tick(pitchId), POLL_INTERVAL_MS);
  }, [_tick]);

  // Tab visibility: pause when hidden, resume when visible if still
  // pending AND still within the 2-minute window.
  useEffect(() => {
    const onVis = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        _stopPolling();
      } else if (pitch && pitch.roadmap_status === 'pending' && !pollTimedOut) {
        _startPolling(pitch.id);
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [pitch, pollTimedOut, _startPolling, _stopPolling]);

  // Cleanup on unmount.
  useEffect(() => () => _stopPolling(), [_stopPolling]);

  // Also stop polling if we leave the page while pending.
  useEffect(() => {
    const onHide = () => _stopPolling();
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', onHide);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onHide);
      }
    };
  }, [_stopPolling]);

  // ── Actions ──
  const refreshNow = useCallback(async () => {
    if (!pitch) return;
    try {
      pollStartRef.current = Date.now();
      setPollTimedOut(false);
      const row = await connectApi.getPitch(pitch.id);
      _applyPitch(row);
    } catch (e) {
      setBootErr(_friendlyErr(e, 'Could not refresh.'));
    }
  }, [pitch, _applyPitch]);

  const saveEdits = useCallback(async () => {
    if (!pitch || !draft) return;
    setSaveBusy(true); setSaveErr(null); setSaveFieldErr(null); setSaveNote(null);
    try {
      const row = await connectApi.patchPitch(pitch.id, {
        subject:           draft.subject || null,
        personal_lines:    draft.personal_lines || null,
        sections_included: draft.sections_included,
        offering_ids:      [...draft.offering_ids],
        insight_ids:       [...draft.insight_ids]
      });
      _applyPitch(row);
      setSaveNote('Saved.');
    } catch (e) {
      if (e && e.code === 'BAD_VALUE') {
        setSaveFieldErr({ field: e.field || null, error: e.message });
      } else if (e && e.code === 'BAD_PITCH_STATE') {
        setSaveErr('This pitch is no longer a draft. Refresh to see the latest state.');
      } else {
        setSaveErr(_friendlyErr(e, 'Could not save.'));
      }
    } finally { setSaveBusy(false); }
  }, [pitch, draft, _applyPitch]);

  const saveFocus = useCallback(async () => {
    if (!contact || !focus) return;
    setFocusBusy(true); setFocusErr(null); setFocusNote(null);
    try {
      const updated = await connectApi.patchContact(contact.id || contact._id, {
        industry:    focus.industry    || null,
        intent:      focus.intent      || null,
        challenge:   focus.challenge   || null,
        refine_note: focus.refine_note || null,
        next_step:   focus.next_step   || null,
        due_date:    focus.due_date    || null
      });
      setContact(updated);
      setFocusNote('Focus saved. Regenerate the roadmap to pick up the new controls.');
    } catch (e) {
      if (e && e.code === 'BAD_VALUE') {
        setFocusErr(e.message || 'That value isn\'t allowed.');
      } else {
        setFocusErr(_friendlyErr(e, 'Could not save Focus.'));
      }
    } finally { setFocusBusy(false); }
  }, [contact, focus]);

  const regenerate = useCallback(async ({ refreshItems = false } = {}) => {
    if (!pitch) return;
    setRegenBusy(true); setRegenErr(null);
    try {
      // Server returns the new draft with roadmap_status='pending'.
      // Reset the poll start-time so the 2-minute budget restarts.
      pollStartRef.current = Date.now();
      setPollTimedOut(false);
      const row = await connectApi.regeneratePitch(pitch.id, { refreshItems });
      _applyPitch(row);
    } catch (e) {
      if (e && e.code === 'PITCH_ALREADY_SENT') {
        setRegenErr('This pitch was already sent -- start a fresh one.');
      } else {
        setRegenErr(_friendlyErr(e, 'Could not regenerate.'));
      }
    } finally { setRegenBusy(false); }
  }, [pitch, _applyPitch]);

  const openPreview = useCallback(async () => {
    if (!pitch) return;
    setPreviewBusy(true); setPreviewErr(null);
    try {
      const r = await connectApi.previewLinkPitch(pitch.id);
      if (r && r.url && typeof window !== 'undefined') {
        window.open(r.url, '_blank', 'noopener');
      }
    } catch (e) {
      setPreviewErr(_friendlyErr(e, 'Could not open preview.'));
    } finally { setPreviewBusy(false); }
  }, [pitch]);

  // Core send call -- extracted so "Send anyway" can retry with
  // allow_despite_intro:true after an INTRO_ALREADY_SENT prompt.
  const _doSend = useCallback(async ({ channel, allow_despite_intro }) => {
    if (!pitch) return;
    setSendBusy(true); setSendErr(null); setSendPrompt(null); setSendResult(null);
    try {
      const r = await connectApi.sendPitch(pitch.id, {
        channel,
        allow_despite_intro: !!allow_despite_intro
      });
      setSendResult(r);
      if (r && r.pitch) setPitch({ ...pitch, ...r.pitch });
      // WhatsApp handoff: open wa.me so the owner can paste.
      if ((channel === 'whatsapp' || channel === 'both') && r && r.whatsapp_text && r.pitch) {
        const phone = (contact && contact.phone ? String(contact.phone) : '').replace(/[^0-9]/g, '');
        if (phone && typeof window !== 'undefined') {
          const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(r.whatsapp_text)}`;
          window.open(waUrl, '_blank', 'noopener');
        }
      }
    } catch (e) {
      _mapSendError(e, { setSendErr, setSendPrompt, channel });
    } finally { setSendBusy(false); }
  }, [pitch, contact]);

  const startSend = useCallback(async () => {
    await _doSend({ channel: sendChannel, allow_despite_intro: false });
  }, [_doSend, sendChannel]);

  const retrySendAllowIntro = useCallback(async () => {
    await _doSend({ channel: sendChannel, allow_despite_intro: true });
  }, [_doSend, sendChannel]);

  // ── Render ──
  if (bootErr) {
    return (
      <section>
        <BackLink id={id} />
        <div style={styles.errorBox}>{bootErr}</div>
        <button type="button" style={styles.btnPrimary} onClick={bootstrap}>Try again</button>
      </section>
    );
  }
  if (!contact || !pitch || !draft || !focus) {
    return (
      <section>
        <BackLink id={id} />
        <div style={styles.muted}>Loading…</div>
      </section>
    );
  }

  const roadmapStatus = pitch.roadmap_status || 'ready';
  const isSent = pitch.status && pitch.status !== 'draft';
  const canSend  = !isSent && roadmapStatus === 'ready';
  const canEdit  = !isSent;
  const canRegen = !isSent && roadmapStatus !== 'pending';

  return (
    <section>
      <BackLink id={id} />

      <h1 style={styles.h1}>Prepare invite</h1>
      <div style={{ color: '#64748b', fontSize: 14, marginBottom: 10 }}>
        For <strong style={{ color: '#0f172a' }}>{contact.full_name || <em>(no name)</em>}</strong>
        {contact.organisation ? ` · ${contact.organisation}` : ''}
        {contact.role ? ` · ${contact.role}` : ''}
      </div>
      {isSent && (
        <div style={styles.infoBox}>
          This pitch has been <strong>{pitch.status}</strong>. Open the contact
          to start a fresh invite.
        </div>
      )}
      {contact.opted_out && (
        <div style={styles.warnBox}>
          This contact is on the opt-out list. Sending is blocked.
        </div>
      )}

      {/* ─── Roadmap ──────────────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader
          title="Roadmap"
          right={canRegen ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={styles.btnSecondary} disabled={regenBusy}
                      onClick={() => regenerate({ refreshItems: false })}>
                {regenBusy ? 'Working…' : 'Regenerate roadmap'}
              </button>
              <button type="button" style={styles.btnGhost} disabled={regenBusy}
                      onClick={() => regenerate({ refreshItems: true })}
                      title="Also replace catalog picks (offerings + insights) with freshly derived candidates.">
                Regenerate + refresh picks
              </button>
            </div>
          ) : null}
        />
        {roadmapStatus === 'pending' && !pollTimedOut && (
          <div style={styles.muted}>
            Building their roadmap…
            <div style={{ fontSize: 12, marginTop: 4 }}>
              This usually takes 12–20 seconds. Keep this tab open; polling resumes
              when the tab is visible.
            </div>
          </div>
        )}
        {roadmapStatus === 'pending' && pollTimedOut && (
          <div style={styles.warnBox}>
            Still working — check back shortly.{' '}
            <button type="button" style={styles.inlineBtn} onClick={refreshNow}>Refresh</button>
          </div>
        )}
        {roadmapStatus === 'failed' && (
          <div style={styles.errorBox}>
            Roadmap not generated — try again.{' '}
            <button type="button" style={styles.inlineBtn} disabled={regenBusy}
                    onClick={() => regenerate({ refreshItems: false })}>
              Regenerate
            </button>
          </div>
        )}
        {roadmapStatus === 'ready' && (
          <RoadmapSteps roadmap={pitch.roadmap_json} />
        )}
        {regenErr && <div style={styles.errorBox}>{regenErr}</div>}
      </div>

      {/* ─── Focus (patches contact) ─────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Focus (steers the drafter)" />
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
          Focus steers the invite drafter and the welcome page. Industry is free
          text; the server canonicalises it before matching offerings.
        </p>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Industry</span>
          <input value={focus.industry}
                 onChange={(e) => setFocus({ ...focus, industry: e.target.value })}
                 placeholder="e.g. Handicrafts & Lifestyle"
                 style={styles.input} maxLength={120} disabled={!canEdit} />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Intent</span>
          <select value={focus.intent}
                  onChange={(e) => setFocus({ ...focus, intent: e.target.value })}
                  style={styles.input} disabled={!canEdit}>
            {INTENTS.map((o) => <option key={o.v || 'unset'} value={o.v}>{o.label}</option>)}
          </select>
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Challenge (one line)</span>
          <input value={focus.challenge}
                 onChange={(e) => setFocus({ ...focus, challenge: e.target.value })}
                 placeholder="One sentence on the hard thing to solve"
                 style={styles.input} maxLength={200} disabled={!canEdit} />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Refine note (optional, not shown to contact)</span>
          <textarea value={focus.refine_note}
                    onChange={(e) => setFocus({ ...focus, refine_note: e.target.value })}
                    rows={2} style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                    maxLength={600} disabled={!canEdit} />
        </label>
        {/* R15 Phase 1 -- next_step + due_date. Rendered as
            {{next_steps}} + {{due_date}} on the catalog intro
            email (when CONNECT_EMAIL_CATALOG_ENABLED=true). */}
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Next step (what we agreed to do)</span>
          <input value={focus.next_step}
                 onChange={(e) => setFocus({ ...focus, next_step: e.target.value })}
                 placeholder="e.g. Send a product demo deck"
                 style={styles.input} maxLength={240} disabled={!canEdit} />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>By (optional due date)</span>
          <input type="date" value={focus.due_date}
                 onChange={(e) => setFocus({ ...focus, due_date: e.target.value })}
                 style={styles.input} disabled={!canEdit} />
        </label>
        {focusErr  && <div style={styles.errorBox}>{focusErr}</div>}
        {focusNote && <div style={styles.okBox}>{focusNote}</div>}
        <div style={{ marginTop: 10 }}>
          <button type="button" style={styles.btnSecondary} disabled={!canEdit || focusBusy}
                  onClick={saveFocus}>
            {focusBusy ? 'Saving…' : 'Save Focus'}
          </button>
        </div>
      </div>

      {/* ─── Subject + personal lines ─────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Email subject + personal lines" />
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Subject</span>
          <input value={draft.subject}
                 onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                 placeholder="(server will draft one if left blank)"
                 style={styles.input} maxLength={240} disabled={!canEdit} />
          <FieldErr err={saveFieldErr} match="subject" />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Personal lines</span>
          <textarea value={draft.personal_lines}
                    onChange={(e) => setDraft({ ...draft, personal_lines: e.target.value })}
                    rows={4} placeholder="Short, personal — appear above the roadmap."
                    style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
                    maxLength={2000} disabled={!canEdit} />
          {draft.personal_lines && draft.personal_lines.trim() && (
            <div style={styles.hint}>
              Drafted from your notes — edit before sending.
            </div>
          )}
          <FieldErr err={saveFieldErr} match="personal_lines" />
        </label>
      </div>

      {/* ─── Sections toggles ──────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Sections on the welcome page" />
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          {SECTION_KEYS.map(({ k, label }) => (
            <label key={k} style={styles.toggleRow}>
              <input type="checkbox" checked={!!draft.sections_included[k]}
                     onChange={(e) => setDraft({
                       ...draft,
                       sections_included: { ...draft.sections_included, [k]: e.target.checked }
                     })}
                     disabled={!canEdit} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <FieldErr err={saveFieldErr} match="sections_included" />
      </div>

      {/* ─── Offerings ────────────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Offerings" />
        {!(pitch.offerings || []).length && (
          <div style={styles.muted}>No offerings suggested — add some from the admin catalog.</div>
        )}
        <div style={{ display: 'grid', gap: 8 }}>
          {(pitch.offerings || []).map((o) => {
            const checked = draft.offering_ids.has(String(o.id));
            return (
              <label key={o.id} style={styles.pickRow}>
                <input type="checkbox" checked={checked}
                       onChange={(e) => {
                         const next = new Set(draft.offering_ids);
                         if (e.target.checked) next.add(String(o.id));
                         else next.delete(String(o.id));
                         setDraft({ ...draft, offering_ids: next });
                       }}
                       disabled={!canEdit} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f172a', fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {o.name}
                    <StatusPill status={o.status} line={o.status_line} />
                    {o.pilot_open && <PilotPill />}
                  </div>
                  {o.one_liner && (
                    <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{o.one_liner}</div>
                  )}
                  <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                    section: {o.section || 'services'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <FieldErr err={saveFieldErr} match="offering_ids" />
      </div>

      {/* ─── Insights ────────────────────────────────────────── */}
      <div style={styles.card}>
        <SectionHeader title="Insights / research" />
        {!(pitch.insights || []).length && (
          <div style={styles.muted}>No insights or research match this contact yet.</div>
        )}
        <div style={{ display: 'grid', gap: 8 }}>
          {(pitch.insights || []).map((i) => {
            const checked = draft.insight_ids.has(String(i.id));
            return (
              <label key={i.id} style={styles.pickRow}>
                <input type="checkbox" checked={checked}
                       onChange={(e) => {
                         const next = new Set(draft.insight_ids);
                         if (e.target.checked) next.add(String(i.id));
                         else next.delete(String(i.id));
                         setDraft({ ...draft, insight_ids: next });
                       }}
                       disabled={!canEdit} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f172a', fontWeight: 600 }}>
                    {i.title} <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 12 }}>({i.kind})</span>
                  </div>
                  {i.one_liner && <div style={{ color: '#64748b', fontSize: 13 }}>{i.one_liner}</div>}
                </div>
              </label>
            );
          })}
        </div>
        <FieldErr err={saveFieldErr} match="insight_ids" />
      </div>

      {/* ─── Save / Preview / Send actions ───────────────────── */}
      <div style={styles.actionsBar}>
        <button type="button" style={styles.btnPrimary}
                disabled={!canEdit || saveBusy}
                onClick={saveEdits}>
          {saveBusy ? 'Saving…' : 'Save edits'}
        </button>
        <button type="button" style={styles.btnSecondary}
                disabled={previewBusy}
                onClick={openPreview}>
          {previewBusy ? 'Opening…' : 'Preview'}
        </button>
        <button type="button" style={styles.btnPrimary}
                disabled={!canSend || contact.opted_out}
                onClick={() => { setSendOpen(true); setSendErr(null); setSendPrompt(null); setSendResult(null); }}
                title={!canSend
                  ? (isSent ? 'Already sent.' : 'Send is enabled only when the roadmap is ready.')
                  : (contact.opted_out ? 'Contact has opted out.' : 'Open the send dialog.')}>
          Send…
        </button>
      </div>
      {saveNote && <div style={styles.okBox}>{saveNote}</div>}
      {saveErr && <div style={styles.errorBox}>{saveErr}</div>}
      {previewErr && <div style={styles.errorBox}>{previewErr}</div>}

      {sendOpen && (
        <SendDialog
          contact={contact}
          channel={sendChannel}
          onChannel={setSendChannel}
          busy={sendBusy}
          err={sendErr}
          prompt={sendPrompt}
          result={sendResult}
          onClose={() => setSendOpen(false)}
          onSend={startSend}
          onSendAnyway={retrySendAllowIntro}
          onBackToContact={() => navigate(`/connect/contacts/${encodeURIComponent(id)}`)}
        />
      )}
    </section>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────────

function BackLink({ id }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <Link to={`/connect/contacts/${encodeURIComponent(id)}`} style={styles.link}>&larr; Back to contact</Link>
    </div>
  );
}

function SectionHeader({ title, right }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: 10, flexWrap: 'wrap', marginBottom: 10
    }}>
      <h3 style={{ margin: 0, color: '#0f172a' }}>{title}</h3>
      {right}
    </div>
  );
}

function RoadmapSteps({ roadmap }) {
  const steps = useMemo(() => {
    if (!roadmap) return [];
    if (roadmap.display && Array.isArray(roadmap.display.steps)) return roadmap.display.steps;
    if (Array.isArray(roadmap.steps)) return roadmap.steps;
    return [];
  }, [roadmap]);

  if (!steps.length) {
    return <div style={styles.muted}>Roadmap has no steps.</div>;
  }
  return (
    <ol style={styles.stepList}>
      {steps.map((s, idx) => (
        <li key={idx} style={styles.stepItem}>
          <div style={styles.stepTitle}>{s.title || `Step ${idx + 1}`}</div>
          {Array.isArray(s.bullets) && s.bullets.length > 0 && (
            <ul style={styles.bulletList}>
              {s.bullets.map((b, i) => <li key={i} style={{ color: '#475569' }}>{b}</li>)}
            </ul>
          )}
          {s.how_we_help && (
            <div style={styles.howWeHelp}>
              <strong style={{ color: '#0e7490' }}>How we help:</strong> {s.how_we_help}
            </div>
          )}
          {s.why && (
            <div style={styles.why}><strong>Why:</strong> {s.why}</div>
          )}
        </li>
      ))}
    </ol>
  );
}

function StatusPill({ status, line }) {
  const live = status === 'live';
  const bg = live ? '#ecfeff' : '#f1f5f9';
  const fg = live ? '#0e7490' : '#64748b';
  const label = live ? 'Live' : (status === 'planned' ? 'Coming soon' : (status || ''));
  return (
    <span title={line || ''} style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: bg, color: fg, fontSize: 11, fontWeight: 600
    }}>{label}</span>
  );
}

function PilotPill() {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: '#fef9c3', color: '#92400e', fontSize: 11, fontWeight: 600
    }}>Pilot open</span>
  );
}

function FieldErr({ err, match }) {
  if (!err || err.field !== match) return null;
  return <div style={styles.fieldErr}>{err.error || 'Invalid value.'}</div>;
}

function SendDialog({
  contact, channel, onChannel, busy, err, prompt, result,
  onClose, onSend, onSendAnyway, onBackToContact
}) {
  const canEmail = !!contact.email && !contact.opted_out;
  const canWa    = !!contact.phone && !contact.opted_out;
  const sent = result && result.pitch && result.pitch.sent_at;

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, color: '#0f172a' }}>Send invite</h3>
        {sent ? (
          <div>
            <div style={styles.okBox}>
              Sent. {channel === 'whatsapp'
                ? 'WhatsApp Web opened in a new tab — paste the message.'
                : channel === 'both'
                  ? 'Email queued and WhatsApp Web opened in a new tab.'
                  : (result.mail === 'queued' ? 'Email is queued.' : 'Email dispatch reported a problem — the welcome page still works; try the preview or resend.')}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" style={styles.btnSecondary} onClick={onClose}>Stay here</button>
              <button type="button" style={styles.btnPrimary} onClick={onBackToContact}>Back to contact</button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: '#475569', fontSize: 14, marginTop: 0 }}>
              Email: <strong>{contact.email || '—'}</strong>
              <br />Phone: <strong>{contact.phone || '—'}</strong>
            </p>
            <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
              <label style={styles.radioRow}>
                <input type="radio" name="ch" checked={channel === 'email'} disabled={!canEmail}
                       onChange={() => onChannel('email')} />
                <span>Email {!canEmail && contact.opted_out ? '(opted out)' : (!contact.email ? '(no email)' : '')}</span>
              </label>
              <label style={styles.radioRow}>
                <input type="radio" name="ch" checked={channel === 'whatsapp'} disabled={!canWa}
                       onChange={() => onChannel('whatsapp')} />
                <span>WhatsApp Web (we open wa.me so you can paste) {!contact.phone ? '(no phone)' : ''}</span>
              </label>
              <label style={styles.radioRow}>
                <input type="radio" name="ch" checked={channel === 'both'} disabled={!canEmail || !canWa}
                       onChange={() => onChannel('both')} />
                <span>Both</span>
              </label>
            </div>

            {err && <div style={styles.errorBox}>{err}</div>}
            {prompt && prompt.kind === 'INTRO_ALREADY_SENT' && (
              <div style={styles.warnBox}>
                An intro was already sent to this contact recently.
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  <button type="button" style={styles.btnSecondary} onClick={onClose}>Not now</button>
                  <button type="button" style={styles.btnPrimary} onClick={onSendAnyway}>Send anyway</button>
                </div>
              </div>
            )}

            {!prompt && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" style={styles.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
                <button type="button" style={styles.btnPrimary} disabled={busy}
                        onClick={onSend}>
                  {busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function _normaliseSections(obj) {
  const defaults = {
    roadmap: true, insights: true, research: true, services: true,
    ai: true, grow: true, resources: true, cta: true
  };
  if (!obj || typeof obj !== 'object') return defaults;
  const out = { ...defaults };
  for (const k of Object.keys(defaults)) {
    if (typeof obj[k] === 'boolean') out[k] = obj[k];
  }
  return out;
}

function _friendlyErr(e, fallback) {
  if (!e) return fallback;
  // 500 bodies carry the generic "Something went wrong" string already
  // (CC rule 13). Preserve the user-facing message when we have one,
  // otherwise show the fallback.
  if (e.message && e.message !== 'Request failed with status code 500') return e.message;
  return fallback;
}

function _mapSendError(e, { setSendErr, setSendPrompt }) {
  const code = e && e.code;
  if (code === 'ROADMAP_NOT_READY') {
    setSendErr('Roadmap isn\'t ready yet — wait for it to finish or regenerate.');
    return;
  }
  if (code === 'OPT_OUT') {
    setSendErr('This contact is on the SkillDelivery opt-out list.');
    return;
  }
  if (code === 'NO_CONTACT_EMAIL') {
    setSendErr('Add an email to this contact first.');
    return;
  }
  if (code === 'NO_CONTACT_PHONE') {
    setSendErr('Add a phone number to this contact first.');
    return;
  }
  if (code === 'INVITE_LIMIT_EXCEEDED') {
    setSendErr(`Daily send cap reached — resets at ${_fmtTime(e)}.`);
    return;
  }
  if (code === 'INTRO_ALREADY_SENT') {
    setSendPrompt({ kind: 'INTRO_ALREADY_SENT' });
    return;
  }
  if (code === 'BAD_PITCH_STATE') {
    setSendErr('This pitch is no longer a draft. Refresh the page.');
    return;
  }
  if (code === 'BAD_CHANNEL') {
    setSendErr('Pick email, WhatsApp Web, or both.');
    return;
  }
  if (code === 'SMART_INVITE_DISABLED') {
    setSendErr('Smart Invite is temporarily unavailable.');
    return;
  }
  setSendErr(_friendlyErr(e, 'Could not send. Try again.'));
}

function _fmtTime(e) {
  const t = e && (e.resetAt || e.resetat);
  if (!t) return 'the daily reset';
  try { return new Date(t).toLocaleString(); } catch (_) { return 'the daily reset'; }
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  h1: { fontSize: 24, color: '#0f172a', margin: '4px 0 2px' },
  link: { color: SKD_CYAN_DARK, textDecoration: 'none', fontSize: 13 },
  card: {
    background: 'white', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0',
    marginBottom: 14
  },
  field: { display: 'block', marginBottom: 10 },
  fieldLabel: {
    display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4,
    textTransform: 'uppercase', letterSpacing: '0.03em'
  },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box'
  },
  hint: {
    fontSize: 12, color: '#92400e', background: '#fef9c3',
    padding: '6px 10px', borderRadius: 8, marginTop: 6, border: '1px solid #fde68a'
  },
  btnPrimary: {
    padding: '8px 16px', borderRadius: 8, background: '#0891b2', color: 'white',
    border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  },
  btnSecondary: {
    padding: '8px 16px', borderRadius: 8, background: 'white', color: '#0e7490',
    border: '1px solid #67e8f9', fontSize: 14, fontWeight: 600, cursor: 'pointer'
  },
  btnGhost: {
    padding: '8px 14px', borderRadius: 8, background: 'transparent', color: '#64748b',
    border: '1px dashed #cbd5e1', fontSize: 13, fontWeight: 500, cursor: 'pointer'
  },
  inlineBtn: {
    marginLeft: 8, padding: '4px 10px', borderRadius: 6, background: 'white',
    border: '1px solid #cbd5e1', color: '#0e7490', fontWeight: 600, cursor: 'pointer', fontSize: 12
  },
  actionsBar: {
    display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0 6px'
  },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginTop: 10, fontSize: 13
  },
  warnBox: {
    padding: 10, background: '#fffbeb', color: '#92400e',
    borderRadius: 8, border: '1px solid #fde68a', marginTop: 10, fontSize: 13
  },
  infoBox: {
    padding: 10, background: '#eff6ff', color: '#1e40af',
    borderRadius: 8, border: '1px solid #bfdbfe', marginBottom: 12, fontSize: 13
  },
  okBox: {
    padding: 10, background: '#f0fdf4', color: '#166534',
    borderRadius: 8, border: '1px solid #bbf7d0', marginTop: 10, fontSize: 13
  },
  fieldErr: {
    marginTop: 4, color: '#991b1b', fontSize: 12
  },
  muted: { color: '#94a3b8', padding: 10, textAlign: 'left', fontSize: 14 },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px', borderRadius: 8, background: '#f8fafc',
    border: '1px solid #e2e8f0', fontSize: 14, color: '#334155'
  },
  pickRow: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    padding: '10px 12px', borderRadius: 8, background: '#f8fafc',
    border: '1px solid #e2e8f0', cursor: 'pointer'
  },
  radioRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px', borderRadius: 8, background: '#f8fafc',
    border: '1px solid #e2e8f0', fontSize: 14, color: '#334155'
  },
  stepList: {
    paddingLeft: 20, margin: 0, display: 'grid', gap: 12
  },
  stepItem: {
    background: '#f8fafc', padding: '10px 14px', borderRadius: 8,
    border: '1px solid #e2e8f0'
  },
  stepTitle: {
    color: '#0f172a', fontWeight: 700, marginBottom: 6
  },
  bulletList: {
    margin: '0 0 6px', paddingLeft: 18, fontSize: 14
  },
  howWeHelp: {
    marginTop: 6, padding: '6px 10px', borderRadius: 6,
    background: '#ecfeff', color: '#0f172a', fontSize: 13,
    border: '1px solid #a5f3fc'
  },
  why: {
    marginTop: 4, fontSize: 13, color: '#64748b'
  },
  modalBackdrop: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20
  },
  modal: {
    width: '100%', maxWidth: 480, background: 'white', borderRadius: 12, padding: 20,
    boxShadow: '0 20px 50px rgba(15,23,42,0.25)'
  }
};
